/**
 * Fase 5 — Sensor-tildeling fra kurs-veiviseren (3-fane modal).
 *
 * Tre faner (synlige kun når bruker har medlemsorg-kontekst):
 *   1. "Velg kollega" — gi eksisterende medlem/admin i egen org sensor-tilgang.
 *      Setter is_sensor=true permanent via POST /admin/users/:id/grant-sensor.
 *      Brukeren kan senere brukes som sensor i alle fremtidige kurs i orgen.
 *   2. "Inviter kollega" — send invitasjon til ny e-post, oppretter en pending
 *      user_invitation med role=member + is_sensor=true. Når invitasjonen aksepteres
 *      blir brukeren hybrid. Hen må lastes inn på nytt senere for å velges manuelt
 *      i et senere kurs (ikke auto-tildeles dette, siden hen ikke er aktiv ennå).
 *   3. "Ekstern fagperson" — dagens flyt: oppretter en ren sensor-rolle direkte
 *      via POST /admin/create-sensor, auto-velger hen for kurset.
 *
 * I alle tilfeller gjelder habilitet-prinsippet: alle sensorer må erklære habilitet
 * ved tildeling, uavhengig av rolle-type. Dette håndteres i sensur-flyten, ikke her.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Loader2, AlertCircle, UserPlus, Users, Mail, UserCheck, CheckCircle2 } from 'lucide-react';
import { authenticatedFetch } from '../../../lib/auth';
import { useToast } from '../../ui';

type TabKey = 'colleague' | 'invite' | 'external';

interface NonSensorUser {
  id: string;
  email: string;
  full_name: string;
  role: 'member' | 'admin';
  is_member_admin: boolean;
}

export interface InlineCreateSensorModalProps {
  open: boolean;
  onClose: () => void;
  /** Context-medlem — avgjør hvilke faner som vises. Uten dette: kun fane 3. */
  memberIdForContext?: string;
  /**
   * Kalles når en sensor er tildelt kurset. Får sensor-info så parent kan
   * auto-velge hen i veiviseren. Ved fane 2 (invite) kalles IKKE denne —
   * invitasjonen må aksepteres først før hen kan velges manuelt senere.
   */
  onCreated: (newSensor: { id: string; full_name: string; email: string; member_id: string | null }) => void;
}

export default function InlineCreateSensorModal({
  open,
  onClose,
  memberIdForContext,
  onCreated,
}: InlineCreateSensorModalProps) {
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>(memberIdForContext ? 'colleague' : 'external');

  // Shared state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fane 1 — Velg kollega
  const [colleagues, setColleagues] = useState<NonSensorUser[]>([]);
  const [loadingColleagues, setLoadingColleagues] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmGrantId, setConfirmGrantId] = useState<string | null>(null);

  // Fane 2 — Inviter ny sensor (ren sensor-rolle, ikke hybrid). Mottakeren fyller inn
  // eget navn ved aksept av invitasjonen, så vi trenger ikke full_name her.
  const [inviteEmail, setInviteEmail] = useState('');

  // Fane 3 — Ekstern fagperson
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Hybrid-flagg: er sensor også ansatt i egen org? Styrer kun habilitet-trigger,
  // ikke synlighet (synlighet er alltid per-org — ekstern blir del av din pool).
  const [isEmployee, setIsEmployee] = useState(false);

  const reset = () => {
    setSaving(false);
    setError(null);
    setSearch('');
    setConfirmGrantId(null);
    setInviteEmail('');
    setFullName('');
    setEmail('');
    setPhone('');
    setIsEmployee(false);
    setTab(memberIdForContext ? 'colleague' : 'external');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // Load colleagues when modal opens on fane 1
  useEffect(() => {
    if (!open || !memberIdForContext || tab !== 'colleague') return;
    (async () => {
      setLoadingColleagues(true);
      setError(null);
      try {
        const r = await authenticatedFetch(`/api/admin/members/${memberIdForContext}/users/non-sensors`);
        const j = await r.json();
        if (!r.ok || !j.success) throw new Error(j.error || 'Kunne ikke laste kolleger');
        setColleagues(Array.isArray(j.users) ? j.users : []);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Kunne ikke laste kolleger');
      } finally {
        setLoadingColleagues(false);
      }
    })();
  }, [open, memberIdForContext, tab]);

  // === Fane 1: Velg kollega — grant-sensor ===
  const handleGrantSensor = async (user: NonSensorUser) => {
    setSaving(true);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/users/${user.id}/grant-sensor`, {
        method: 'POST',
      });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.error || 'Kunne ikke gi sensor-tilgang');

      toast.showSuccess(
        `${user.full_name} har fått sensor-tilgang og er automatisk valgt for dette kurset.`
      );
      onCreated({
        id: user.id,
        full_name: user.full_name,
        email: user.email,
        member_id: memberIdForContext ?? null,
      });
      reset();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ukjent feil';
      setError(msg);
      toast.showError('Kunne ikke gi sensor-tilgang: ' + msg);
    } finally {
      setSaving(false);
      setConfirmGrantId(null);
    }
  };

  // === Fane 2: Inviter ny sensor-bruker (ren sensor-rolle, ikke hybrid) ===
  const handleInvite = async () => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(inviteEmail.trim());
    if (!emailOk) {
      setError('E-postadressen må være gyldig.');
      return;
    }
    if (!memberIdForContext) {
      setError('Mangler medlemsorganisasjon-kontekst.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const r = await authenticatedFetch(
        `/api/admin/members/${memberIdForContext}/users/invite`,
        {
          method: 'POST',
          body: JSON.stringify({
            email: inviteEmail.trim(),
            role: 'sensor',
          }),
        }
      );
      const j = await r.json();
      if (!r.ok || !j.success) {
        throw new Error(j.error || j.message || 'Kunne ikke sende invitasjon');
      }
      toast.showSuccess(
        `Invitasjon sendt til ${inviteEmail.trim()}. Når invitasjonen aksepteres opprettes en ny sensor-bruker. Hen kan velges i et senere kurs.`
      );
      reset();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Ukjent feil';
      setError(msg);
      toast.showError('Invitasjon feilet: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  // === Fane 3: Ekstern fagperson — eksisterende flyt ===
  const handleCreateExternal = async () => {
    if (!fullName.trim() || fullName.trim().length < 2) {
      setError('Fullt navn må være minst 2 tegn.');
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
    if (!emailOk) {
      setError('E-postadressen må være gyldig (f.eks. navn@eksempel.no).');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await authenticatedFetch('/api/admin/create-sensor', {
        method: 'POST',
        body: JSON.stringify({
          full_name: fullName.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          member_id: memberIdForContext || undefined,
          is_employee: isEmployee,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || json.message || 'Kunne ikke opprette sensor');
      }
      const created = json.user || json.sensor || json.data || {};
      toast.showSuccess(
        `${fullName.trim()} er opprettet som sensor og automatisk valgt for dette kurset. E-post for passord-oppsett er sendt til ${email.trim()}.`
      );
      onCreated({
        id: created.id,
        full_name: created.full_name || fullName.trim(),
        email: created.email || email.trim(),
        member_id: created.member_id ?? null,
      });
      reset();
      onClose();
    } catch (err_: unknown) {
      const msg = err_ instanceof Error ? err_.message : 'Ukjent feil';
      setError(msg);
      toast.showError('Opprettelse feilet: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // Filtrer kolleger på søk
  const filteredColleagues = colleagues.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      c.full_name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q)
    );
  });

  const confirmUser = confirmGrantId
    ? colleagues.find(c => c.id === confirmGrantId)
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-stretch sm:items-center justify-center sm:p-4 bg-kf-gray-900/60 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-2xl flex flex-col h-full sm:h-auto sm:max-h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        <header className="px-4 sm:px-6 py-4 border-b border-kf-gray-200 bg-kf-blue-50 sm:rounded-t-xl flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-kf-blue-700 text-sm font-semibold">
              <UserPlus className="w-4 h-4" />
              Legg til sensor
            </div>
            <p className="text-sm text-kf-gray-700 mt-1">
              Velg en kollega som allerede jobber her, inviter en ny, eller legg til en ekstern fagperson.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="shrink-0 p-1.5 rounded hover:bg-white text-kf-gray-600"
            aria-label="Lukk"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Tab-nav — vises kun hvis vi har memberIdForContext */}
        {memberIdForContext && (
          <nav className="px-4 sm:px-6 pt-3 border-b border-kf-gray-200 bg-white flex gap-1 overflow-x-auto">
            <button
              type="button"
              onClick={() => { setTab('colleague'); setError(null); }}
              className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                tab === 'colleague'
                  ? 'border-kf-blue-600 text-kf-blue-700'
                  : 'border-transparent text-kf-gray-600 hover:text-kf-gray-800'
              }`}
              title="Gi en kollega som allerede er registrert i systemet, tilleggsrolle som sensor"
            >
              <Users className="w-4 h-4" />
              Eksisterende bruker
            </button>
            <button
              type="button"
              onClick={() => { setTab('invite'); setError(null); }}
              className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                tab === 'invite'
                  ? 'border-kf-blue-600 text-kf-blue-700'
                  : 'border-transparent text-kf-gray-600 hover:text-kf-gray-800'
              }`}
              title="Inviter en ny person som får en helt ny brukerkonto med kun sensor-rolle"
            >
              <Mail className="w-4 h-4" />
              Inviter ny sensor
            </button>
            <button
              type="button"
              onClick={() => { setTab('external'); setError(null); }}
              className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
                tab === 'external'
                  ? 'border-kf-blue-600 text-kf-blue-700'
                  : 'border-transparent text-kf-gray-600 hover:text-kf-gray-800'
              }`}
              title="Opprett en frilanser/konsulent direkte som ekstern sensor"
            >
              <UserCheck className="w-4 h-4" />
              Ekstern fagperson
            </button>
          </nav>
        )}

        <div className="px-4 sm:px-6 py-5 flex-1 overflow-y-auto space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="whitespace-pre-line">{error}</span>
            </div>
          )}

          {/* ================== FANE 1: Eksisterende bruker ================== */}
          {tab === 'colleague' && memberIdForContext && (
            <>
              <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 px-3 py-2 text-xs text-kf-blue-900">
                <p className="font-medium mb-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Gi en <u>eksisterende bruker</u> tilleggsrolle som sensor
                </p>
                <p>
                  Dette er personer som <strong>allerede har en konto i systemet</strong> som
                  medlem eller medlem-administrator i din organisasjon. De beholder sin
                  primærrolle og får sensor-tilgang <strong>i tillegg</strong> (hybrid-rolle).
                  Tilgangen kan fjernes senere via <em>Min organisasjon → Brukere → Fjern sensor</em>.
                </p>
                <p className="mt-1 text-kf-blue-800">
                  Velg denne når en kollega som i dag jobber med studieplaner/kurs også skal kunne
                  sensurere.
                </p>
              </div>

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Søk etter navn eller e-post…"
                className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500"
              />

              {loadingColleagues ? (
                <div className="flex items-center gap-2 text-sm text-kf-gray-600 py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Laster kolleger…
                </div>
              ) : filteredColleagues.length === 0 ? (
                <div className="rounded-md bg-kf-gray-50 border border-kf-gray-200 px-3 py-4 text-sm text-kf-gray-700 text-center">
                  {colleagues.length === 0
                    ? 'Ingen kolleger i organisasjonen har ennå ikke sensor-tilgang.'
                    : 'Ingen treff. Prøv et annet søkeord.'}
                </div>
              ) : (
                <div className="border border-kf-gray-200 rounded-lg divide-y divide-kf-gray-100 max-h-80 overflow-y-auto">
                  {filteredColleagues.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setConfirmGrantId(c.id)}
                      className="w-full text-left px-3 py-2 hover:bg-kf-blue-50 flex items-center gap-3"
                    >
                      <UserCheck className="w-5 h-5 text-kf-blue-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-kf-gray-900 text-sm">{c.full_name}</span>
                          {c.is_member_admin && (
                            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200">
                              Medlem-admin
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-kf-gray-500">{c.email}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ================== FANE 2: Inviter ny sensor-bruker ================== */}
          {tab === 'invite' && memberIdForContext && (
            <>
              <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 px-3 py-2 text-xs text-kf-blue-900">
                <p className="font-medium mb-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Opprett en <u>ny bruker</u> som kun får sensor-rolle
                </p>
                <p className="mb-1">
                  Dette er personer som <strong>ikke har konto i systemet fra før</strong>.
                  Ved aksept av invitasjonen opprettes en <strong>ny brukerkonto med kun
                  sensor-rolle</strong> — ingen medlem- eller admin-tilgang til studieplaner og
                  kurs-oppretting.
                </p>
                <ol className="list-decimal list-inside space-y-0.5 pl-1 mt-2">
                  <li>Mottakeren får e-post med lenke for å opprette konto og sette passord.</li>
                  <li>Når invitasjonen aksepteres, blir hen en <strong>ren sensor-bruker</strong> i din organisasjons sensor-pool.</li>
                  <li>Hen kan deretter velges som sensor i fremtidige kurs.</li>
                  <li>Siden hen ikke eksisterer ennå, må dette kurset tildele en annen sensor nå — eller du kan vente til invitasjonen er akseptert og starte veiviseren på nytt.</li>
                </ol>
                <p className="mt-2 text-kf-blue-800">
                  Velg denne når noen i din organisasjon kun skal sensurere — ikke jobbe med
                  studieplaner eller kurs-oppretting. Trenger hen bredere tilgang, bruk heller
                  <em> Min organisasjon → Brukere</em> for full invitasjon med medlem-rolle.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                  E-postadresse <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="sensor@eksempel.no"
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                />
                <p className="text-xs text-kf-gray-500 mt-1">
                  Sensorer kan ha hvilken som helst gyldig e-postadresse — ingen domene-restriksjon.
                  Mottakeren fyller inn eget navn ved aksept av invitasjonen.
                </p>
              </div>
            </>
          )}

          {/* ================== FANE 3: Ekstern fagperson ================== */}
          {tab === 'external' && (
            <>
              <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 px-3 py-2 text-xs text-kf-blue-900 space-y-1">
                <p className="font-medium flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Opprett en <u>ekstern fagperson</u> direkte (uten invitasjons-flyt)
                </p>
                <p>
                  En frilanser, konsulent eller fagperson som leies inn for sensur, <strong>uten å
                  være ansatt i din organisasjon</strong>. Hen opprettes <strong>umiddelbart som
                  aktiv sensor</strong> og får e-post med lenke for å sette eget passord
                  (24 timers gyldighet). Blir del av din organisasjons sensor-pool og
                  <strong> auto-velges for dette kurset</strong>.
                </p>
                <p className="mt-1 text-kf-blue-800">
                  Forskjell fra «Inviter ny sensor»: ekstern fagperson opprettes med én gang (du
                  fyller inn navn + e-post), mens invitasjons-flyten lar personen selv opprette
                  kontoen via lenke i e-post.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                  Fullt navn <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Fornavn Etternavn"
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-kf-gray-700 mb-1">
                  E-postadresse <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="navn@eksempel.no"
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                />
                <p className="text-xs text-kf-gray-500 mt-1">
                  Eksterne sensorer kan ha hvilken som helst gyldig e-postadresse — ingen
                  domene-restriksjon.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-kf-gray-700 mb-1">Telefon (valgfritt)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+47 123 45 678"
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500"
                />
              </div>

              <div className="rounded-md border border-kf-gray-300 p-3 bg-white">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEmployee}
                    onChange={(e) => setIsEmployee(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="font-medium text-sm text-kf-gray-900 block">
                      Sensoren har fast tilknytning til organisasjonen (ansatt, oppdragsavtale eller verv)
                    </span>
                    <span className="text-xs text-kf-gray-600 block mt-1">
                      Hak av hvis sensoren er ansatt hos oss, har løpende oppdragsavtale eller
                      sitter i styreverv — altså ikke er en «ren frilanser» som kun leies inn
                      for dette oppdraget. Da får du en ekstra habilitet-varsel ved tildeling,
                      med krav om skriftlig begrunnelse, fordi den faste tilknytningen er en
                      potensiell interessekonflikt.
                    </span>
                    <span className="text-[11px] text-kf-gray-500 block mt-1.5 italic">
                      Habilitet-erklæring (ingen nære relasjoner til kandidatene, rettferdig
                      vurdering) kreves uansett ved alle sensur-oppdrag — denne avkrysningen
                      erstatter ikke den.
                    </span>
                  </span>
                </label>
              </div>
            </>
          )}
        </div>

        <footer className="px-4 sm:px-6 py-3 border-t border-kf-gray-200 bg-kf-gray-50 sm:rounded-b-xl flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-3 py-1.5 text-sm text-kf-gray-700 hover:bg-white rounded"
          >
            Avbryt
          </button>
          {tab === 'invite' && (
            <button
              type="button"
              onClick={handleInvite}
              disabled={saving || !inviteEmail.trim()}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {saving ? 'Sender…' : 'Send invitasjon'}
            </button>
          )}
          {tab === 'external' && (
            <button
              type="button"
              onClick={handleCreateExternal}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Oppretter…' : 'Opprett og inviter'}
            </button>
          )}
          {/* Fane 1 har ingen primærknapp i footer — handling skjer per rad via bekreftelse-modal */}
        </footer>

        {/* Bekreftelse-modal for fane 1 */}
        {confirmUser && (
          <div
            className="fixed inset-0 z-[9100] flex items-center justify-center p-4 bg-kf-gray-900/60"
            onClick={(e) => { if (e.target === e.currentTarget) setConfirmGrantId(null); }}
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5">
              <div className="flex items-start gap-3 mb-3">
                <CheckCircle2 className="w-6 h-6 text-kf-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-kf-gray-900">
                    Gi {confirmUser.full_name} sensor-tilgang?
                  </h3>
                  <p className="text-sm text-kf-gray-700 mt-2">
                    Kollegaen får sensor-rolle i din organisasjon <strong>permanent</strong>, i tillegg
                    til nåværende rolle som {confirmUser.is_member_admin ? 'medlem-administrator' : 'medlem'}.
                    Hen vil kunne velges som sensor i alle fremtidige kurs. Rollen kan fjernes senere
                    via <em>Min organisasjon → Brukere</em>.
                  </p>
                  <p className="text-sm text-kf-gray-700 mt-2">
                    Hen blir også <strong>automatisk valgt for dette kurset</strong>.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-kf-gray-200">
                <button
                  type="button"
                  onClick={() => setConfirmGrantId(null)}
                  className="px-3 py-1.5 text-sm text-kf-gray-700 hover:bg-kf-gray-50 rounded"
                  disabled={saving}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={() => handleGrantSensor(confirmUser)}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-1.5 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                  {saving ? 'Lagrer…' : 'Gi sensor-tilgang'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
